import { useState } from "react";
import { api } from "../api/client";
import { dateTime } from "../text";
import { useCampaign, useCampaignQuery } from "./context";
import { CampaignError, useAction, useEntries } from "./parts";
import type { CampaignLogEntry } from "./types";

/** The log of the campaign with linear undo and redo and the text diff per entry; players see none (F6). */
export function CampaignLogPage() {
  const campaign = useCampaign();
  const player = campaign.mode === "player";
  const log = useCampaignQuery(["log"], (headers) => api.GET("/api/campaigns/{id}/log", { params: { path: { id: campaign.id } }, headers }), !player);
  const action = useAction();
  const { undo, redo } = campaign.head;
  if (player) return <p className="muted">Das Protokoll gibt es in dieser Ansicht nicht.</p>;
  return (
    <div className="stack">
      <div className="actions">
        <button type="button" className="button secondary" disabled={!undo.available || action.busy} onClick={() => void action.run(campaign.undo)}>
          Rückgängig{undo.summary ? `: ${undo.summary}` : ""}
        </button>
        <button type="button" className="button secondary" disabled={!redo.available || action.busy} onClick={() => void action.run(campaign.redo)}>
          Wiederholen{redo.summary ? `: ${redo.summary}` : ""}
        </button>
      </div>
      {(undo.hint || redo.hint) && <p className="muted small">{undo.hint ?? redo.hint}</p>}
      <CampaignError error={action.error} onClose={action.clear} />
      {campaign.mode !== "dossier" && (
        <p className="muted small">
          Änderungen, die Geheimes berühren, stehen nur im Protokoll des Dossiers und lassen sich nur dort rückgängig machen. Dazu gehören Notizen am Tisch, weil sie ins
          geheime Protokoll des laufenden Eintrags gehen ({campaign.term("session")}), und das Ende eines im Dossier vorbereiteten Eintrags der Art {campaign.term("scene")}.
        </p>
      )}
      {log.isLoading && <p className="muted">Lade …</p>}
      {log.error && <CampaignError error={log.error} reload />}
      <ol className="log" aria-label="Protokoll">
        {log.data?.map((entry) => <LogRow key={entry.revision} entry={entry} />)}
      </ol>
    </div>
  );
}

function LogRow({ entry }: { entry: CampaignLogEntry }) {
  const [open, setOpen] = useState(false);
  const campaign = useCampaign();
  const entries = useEntries();
  const title = (id: string | null | undefined) => (id ? entries.data?.find((e) => e.id === id)?.title : undefined);
  const context = entry.context;
  return (
    <li className={`log-entry ${entry.status}`}>
      <span className="log-time">
        {dateTime(entry.at)}
        {entry.userName && <> · {entry.userName}</>}
        {entry.originName && <> · über {entry.originName}</>}
      </span>
      <span className="log-summary">
        <span className="log-text">{entry.summary}</span>
        {entry.count > 1 && <span className="badge">×{entry.count}</span>}
        {entry.status === "undone" && <span className="badge">rückgängig</span>}
        {entry.secret && <span className="badge secret">geheim</span>}
      </span>
      {context && (
        <span className="log-context muted small">
          {[
            context.date.de,
            title(context.sessionId) && `${campaign.term("session")} „${title(context.sessionId)}“`,
            title(context.sceneId) && `${campaign.term("scene")} „${title(context.sceneId)}“`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      )}
      {entry.characters.length > 0 && (
        <ul className="plain-list log-characters" aria-label="Buchungen an Charakteren">
          {entry.characters.map((c) => (
            <li key={`${c.characterId}-${c.entry}`} className="small">
              {c.name}: {c.summary}
              {c.reverted && <span className="badge">zurückgenommen</span>}
            </li>
          ))}
        </ul>
      )}
      <button type="button" className="link log-revert" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        {open ? "Vergleich schließen" : "Textvergleich"}
      </button>
      {open && <Diff revision={entry.revision} />}
    </li>
  );
}

function Diff({ revision }: { revision: number }) {
  const campaign = useCampaign();
  const diff = useCampaignQuery(["diff", revision], (headers) =>
    api.GET("/api/campaigns/{id}/log/{revision}/diff", { params: { path: { id: campaign.id, revision } }, headers }),
  );
  if (diff.isLoading) return <p className="muted small log-diff">Lade …</p>;
  if (diff.error)
    return (
      <div className="log-diff">
        <CampaignError error={diff.error} reload />
      </div>
    );
  if (!diff.data || diff.data.sections.length === 0) return <p className="muted small log-diff">Kein Text geändert.</p>;
  return (
    <div className="log-diff">
      {diff.data.sections.map((s) => (
        <div key={s.sectionId} className="diff">
          <strong>{s.headingAfter ?? s.headingBefore}</strong>
          {s.headingBefore && s.headingAfter && s.headingBefore !== s.headingAfter && <span className="muted small"> (vorher „{s.headingBefore}“)</span>}
          <pre>
            {s.lines.map((line, i) => (
              <span key={i} className={`diff-line ${line.kind}`}>
                {line.kind === "added" ? "+ " : line.kind === "removed" ? "− " : "  "}
                {line.text}
                {"\n"}
              </span>
            ))}
          </pre>
        </div>
      ))}
    </div>
  );
}
